import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import {
  FaEnvelope, FaLock, FaEye, FaEyeSlash,
  FaTruck, FaExclamationCircle,
} from 'react-icons/fa'
import { loginUser, clearError } from 'store/slices/authSlice'
import '../../assets/scss/pages/Auth.scss'

const LoginSchema = Yup.object().shape({
  email:    Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().required('Password is required'),
})

const Login = () => {
  const navigate  = useNavigate()
  const dispatch  = useDispatch()
  const { loading, error, isAuthenticated } = useSelector(state => state.auth)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe,   setRememberMe]   = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      toast.success('Welcome back!')
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  useEffect(() => () => { dispatch(clearError()) }, [dispatch])

  const handleSubmit = values => dispatch(loginUser(values))

  return (
    <div className="lg-wrap">

      {/* ── LEFT BRAND PANEL ── */}
      <div className="lg-panel">
        <div className="lg-panel-top">
          <div className="lg-logo">
            <div className="lg-logo-icon"><FaTruck /></div>
            <div className="lg-logo-name">TradeSetu <span>Transport</span></div>
          </div>

          <h1 className="lg-headline">
            Move freight<br /><em>smarter,</em><br />not harder.
          </h1>
          <p className="lg-sub">
            A unified platform for transport requests, proposals,
            nominations and bilty management — all in one place.
          </p>

          <div className="lg-features">
            {[
              'Real-time Fleet & Driver Tracking',
              'Digital Freight Proposals',
              'Truck Nominations & Bilty',
            ].map(f => (
              <div className="lg-feature" key={f}>
                <div className="lg-feature-dot">✓</div>
                <span className="lg-feature-text">{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg-panel-bottom">
          © {new Date().getFullYear()} TradeSetu · Transport Management Platform
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div className="lg-form-wrap">
        <div className="lg-card">

          {/* mobile logo */}
          <div className="lg-mobile-logo">
            <div className="lg-mobile-logo-icon"><FaTruck /></div>
            <div className="lg-mobile-logo-name">TradeSetu</div>
          </div>

          <div className="lg-card-top">
            <h2 className="lg-card-title">Welcome back</h2>
            <p className="lg-card-sub">Sign in to your account to continue</p>
          </div>

          {error && (
            <div className="lg-error">
              <FaExclamationCircle className="lg-error-icon" />
              {error}
            </div>
          )}

          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isValid, dirty }) => (
              <Form className="lg-form">

                {/* Email */}
                <div className="lg-field">
                  <label className="lg-label">
                    <FaEnvelope /> Email Address
                  </label>
                  <div className="lg-input-wrap">
                    <FaEnvelope className="lg-input-icon" />
                    <Field
                      type="email"
                      name="email"
                      id="email"
                      className={`lg-input ${errors.email && touched.email ? 'lg-input--error' : ''}`}
                      placeholder="you@company.com"
                      autoComplete="email"
                    />
                  </div>
                  {errors.email && touched.email && (
                    <span className="lg-field-error">{errors.email}</span>
                  )}
                </div>

                {/* Password */}
                <div className="lg-field">
                  <label className="lg-label">
                    <FaLock /> Password
                  </label>
                  <div className="lg-input-wrap">
                    <FaLock className="lg-input-icon" />
                    <Field
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      id="password"
                      className={`lg-input ${errors.password && touched.password ? 'lg-input--error' : ''}`}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="lg-pw-toggle"
                      onClick={() => setShowPassword(p => !p)}
                      tabIndex={-1}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <span className="lg-field-error">{errors.password}</span>
                  )}
                </div>

                {/* Remember / Forgot */}
                <div className="lg-options">
                  <label className="lg-check">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                    />
                    <span>Remember me</span>
                  </label>
                  <Link to="/forgot-password" className="lg-forgot">Forgot password?</Link>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="lg-btn"
                  disabled={loading || !isValid || !dirty}
                >
                  {loading
                    ? <><span className="spinner-border spinner-border-sm" />Signing in…</>
                    : 'Sign In'}
                </button>

                <div className="lg-footer">
                  Don&apos;t have an account?{' '}
                  <Link to="/register">Create account</Link>
                </div>

              </Form>
            )}
          </Formik>
        </div>
      </div>

    </div>
  )
}

export default Login
